import {
  isH2StudySubject,
  type H2StudySubject,
} from "@/lib/study/h2-study-topics";

export const STUDY_PROGRESS_STORAGE_KEY = "eclat-study-progress:v1";
export const STUDY_PROGRESS_EVENT = "eclat-study-progress-changed";

export type StudyActivity = "note" | "quiz" | "video" | "mistakes";

export type StudyProgress = {
  version: 1;
  subject: H2StudySubject;
  subjectLabel: string;
  topicId: string;
  topicLabel: string;
  activity: StudyActivity;
  resumeHref: string;
  noteHref: string;
  quizHref: string | null;
  videoHref: string | null;
  updatedAt: string;
};

export type StudyProgressInput = Omit<StudyProgress, "version" | "updatedAt">;

export function parseStudyProgress(raw: string | null): StudyProgress | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<StudyProgress> | null;
    if (!parsed || parsed.version !== 1) return null;
    if (
      typeof parsed.subject !== "string" ||
      !isH2StudySubject(parsed.subject) ||
      typeof parsed.subjectLabel !== "string" ||
      typeof parsed.topicId !== "string" ||
      typeof parsed.topicLabel !== "string" ||
      !["note", "quiz", "video", "mistakes"].includes(parsed.activity ?? "") ||
      typeof parsed.resumeHref !== "string" ||
      typeof parsed.noteHref !== "string" ||
      typeof parsed.updatedAt !== "string" ||
      (parsed.quizHref !== null && typeof parsed.quizHref !== "string") ||
      (parsed.videoHref !== null && typeof parsed.videoHref !== "string")
    ) {
      return null;
    }
    return parsed as StudyProgress;
  } catch {
    return null;
  }
}

export function readStudyProgress(): StudyProgress | null {
  if (typeof window === "undefined") return null;
  try {
    return parseStudyProgress(
      window.localStorage.getItem(STUDY_PROGRESS_STORAGE_KEY)
    );
  } catch {
    return null;
  }
}

export function saveStudyProgress(input: StudyProgressInput): void {
  if (typeof window === "undefined") return;
  const progress: StudyProgress = {
    ...input,
    version: 1,
    updatedAt: new Date().toISOString(),
  };
  try {
    window.localStorage.setItem(
      STUDY_PROGRESS_STORAGE_KEY,
      JSON.stringify(progress)
    );
    window.dispatchEvent(new Event(STUDY_PROGRESS_EVENT));
  } catch {
    // Device storage is optional. The study route still works without it.
  }
}
